import React from 'react';
import useABC from '../Provider/TableContext/ABCContext';
import useResponsibility from '../Provider/TableContext/ResponsibilityContext';
import useCV from '../Provider/TableContext/CVContext';
import useProfile from '../Provider/TableContext/ProfileContext';
import Sectors from '../Constants/Sector.json';
import LegacySectors from '../Constants/Sector.legacy.json';
import useQualification from '../Provider/TableContext/QualificationContext';
import RoleStatus from  '../Constants/RoleStatus.json';
import { AggregateError, UserReadableError } from '../Functions/Error';
import useRole from '../Provider/TableContext/RoleContext';
import { stripHtml } from '../Functions/Helper/RichText';
import useSkill from '../Provider/TableContext/SkillContext';
import useCVSkillAchievement from '../Provider/TableContext/CVSkillAchievementContext';
import { useCVStepFunctions } from './useCVStepFunctions';


export default function useCreatedRoleContext({ role, updateRole, roleStatus, roleStatusName, cvName }) {
    const { cvs, processCVChanges, uploadFileToCV, loading: cvLoading } = useCV();
    const { profileData } = useProfile();
    const { responsibilities, processResponsibilityChanges, loading: responsibilityLoading } = useResponsibility();
    const { roles, loading: roleLoading } = useRole();
    const { qualifications } = useQualification();
    const { skills, loading: skillLoading } = useSkill();
    const { abcs, loading: abcLoading } = useABC();
    const [formData, setFormData] = React.useState({});
    const { cvskillachievements, processCVSkillAchievementChanges } = useCVSkillAchievement();
    const [loading, setLoading] = React.useState(!role || responsibilityLoading || abcLoading || cvLoading);
    const originalCVKSA = React.useMemo(() => {
        if (!role?._mcb_cv_value || formData.CV) return [];
        return cvskillachievements.filter(a => 
            a._mcb_cv_value === role._mcb_cv_value
            || a._mcb_cv_value === formData.CV
        ).sort((a, b) => (a?.mcb_index || 0) - (b.mcb_index || 0)
        );
    }, [cvskillachievements, role?._mcb_cv_value, formData?.CV]);

    const { setCVBytes, updateCV:  _updateCV} = useCVStepFunctions({
        formData,
        setFormData,
        setLoading,
        uploadFileToCV,
        processCVChanges,
        processCVSkillAchievementChanges,
        originalCVKSA
    }) ;

    React.useEffect(() => {
        if (!(!role || roleLoading || responsibilityLoading || abcLoading || skillLoading || cvLoading)) {
            // pre-populate the form with the role details
            // this will run whenever the role is updated in the backend
            // this will also generate the cv
            role.cliftonstrengths = (role?.mcb_cliftonstrength? role.mcb_cliftonstrength.split(",") : []).filter(a => a && !isNaN(a)).map(a => Number(a));
            role.qualifications = (Array.isArray(role?.mcb_TertiaryQualifications) ? role.mcb_TertiaryQualifications.map(({mcb_qualificationid}) => mcb_qualificationid) : []);
            
            const skillCounter = {};
            const potentialSkillsAchievements = {};
            const abcCounter = {};
            // used in cv generation
            const mcb_CVABC = [];
            const achievements = [];
            const abcIndexed = {};
            if (Array.isArray(abcs)) {
                abcs.forEach(abc => {
                    const { mcb_abcid } = abc;
                    abcIndexed[mcb_abcid] = abc;
                    mcb_CVABC.push({ mcb_abcid });
                    achievements.push(mcb_abcid);
                });
            }

            role.requirements = responsibilities.filter(responsibility => {
                return responsibility?._mcb_role_value === role?.mcb_roleid;
            }).map(({mcb_ABCResponsibility, ...responsibility}) => ({
                ...responsibility,
                achievements: (Array.isArray(mcb_ABCResponsibility)? 
                    mcb_ABCResponsibility.map(({ mcb_abcid }) => {
                        abcCounter[mcb_abcid] = (abcCounter[mcb_abcid] || 0) + 1;
                        let abc = Array.isArray(abcs)? abcs.find(({ mcb_abcid: abcId }) => abcId === mcb_abcid) : undefined;
                        if (Array.isArray(abc?.mcb_ABCSkill)) abc.mcb_ABCSkill.forEach(({mcb_skillid}) => {
                            skillCounter[mcb_skillid] = (skillCounter[mcb_skillid] || 0) + 1;
                            // if this is a keySkill
                            if (role?.mcb_KeySkills.some((ks) => ks.mcb_skillid === mcb_skillid)) {
                                const potentialSkillAchievement =  potentialSkillsAchievements[mcb_skillid] || [];
                                if (!potentialSkillAchievement.some(a => a.mcb_abcid === abc.mcb_abcid)) {
                                    potentialSkillAchievement.push(abc);
                                }
                                potentialSkillsAchievements[mcb_skillid] = potentialSkillAchievement;
                            }
                        });
                        return abc;
                    }).filter(a => a) : []
                )
            })).sort((a,b) => (a.mcb_index || 0) - (b.mcb_index || 0));
            
            role.skills = (role?.mcb_KeySkills || [])
                .map(({mcb_skillid}) => mcb_skillid)
                .sort((a, b) => {
                    return (skillCounter[b] || 0) - (skillCounter[a] || 0);
                });

            const cvId = formData.CV || role?._mcb_cv_value;
            let cv  = cvId && Array.isArray(cvs)? cvs.find(cv => cv?.mcb_cvid === cvId) : undefined;
            if (cv) {
                cv = {...cv, keySkillsAchievements : cvskillachievements.filter(a =>
                    a._mcb_cv_value === cv?.mcb_cvid).sort((a,b) => (a?.mcb_index || 0) - (b.mcb_index||0)
                ) };
                if (formData.Duplicate) {
                    cv.mcb_cvid = undefined;
                    cv.mcb_cvname = cv.mcb_cvname + " - Copy";
                }
            } 
            
            if (!cv || formData.Duplicate) {
                // select all the roles that are current or previous to be added to the CV by default
                const mcb_CVRoles = [];
                const cvRoles = [];

                roles.filter(({mcb_rolestatus}) => 
                    mcb_rolestatus === RoleStatus.Current || mcb_rolestatus === RoleStatus.Previous
                ).forEach(({mcb_roleid}) => {
                    mcb_CVRoles.push({ mcb_roleid });
                    cvRoles.push(mcb_roleid);
                });
                
                // select all qualifications that are associated with the role to be added to the CV by default
                const cvMainQualifications = qualifications
                    .filter(({mcb_qualificationid, mcb_abbreviation}) => 
                        Array.isArray(role?.mcb_TertiaryQualifications) && role.mcb_TertiaryQualifications.some(({mcb_qualificationid: qid}) => 
                            qid === mcb_qualificationid
                        ) && mcb_abbreviation
                    ).sort((a,b) => new Date(b.mcb_dateearned) - new Date(a.mcb_dateearned))
                    .map(({mcb_abbreviation}) => mcb_abbreviation).join(", ");
                
                let keySkillsAchievements = [];
                const bookedAchievements = [];

                // filling in the key skills achievements from duplicated CV if exists.
                if (cv && Array.isArray(cv.keySkillsAchievements)) {
                    cv.keySkillsAchievements.forEach((cvKSA) => {
                        const {_mcb_skill_value, _mcb_abc_value} = cvKSA;
                        if (_mcb_skill_value && _mcb_abc_value) {
                            delete potentialSkillsAchievements[_mcb_skill_value];
                            bookedAchievements.push(_mcb_abc_value);
                            keySkillsAchievements.push(cvKSA);
                        }
                    });
                }

                // sort the potential skill achievements based on the number of times the skill is associated with the requirements, and then by the order of the requirements, and pick the top 5 to be added to the CV by default
                Object.entries(potentialSkillsAchievements)
                    .sort(([skill1], [skill2]) => skillCounter[skill1] - skillCounter[skill2])
                    .forEach(([mcb_skillid, abcs]) => {
                        abcs = abcs.sort((a, b) => abcCounter[a.mcb_abcid] - abcCounter[b.mcb_abcid] || (a.mcb_index || 0) - (b.mcb_index || 0));
                        for (let c = 0; c < abcs.length; c++) {
                            const abc = abcs[c];
                            if (!bookedAchievements.includes(abc.mcb_abcid)) {
                                bookedAchievements.push(abc.mcb_abcid);
                                const abcData = stripHtml(abc.mcb_achievementstatement);
                                const skillName = skills.find(a => a.mcb_skillid === mcb_skillid)?.mcb_skill;
                                const mcb_fullstatement = `<p><strong>${skillName}: </strong><span>${abcData}</span></p>`;
                                const mcb_statement = `${skillName}: ${abcData}`.slice(0,100);
                                keySkillsAchievements.push({
                                    mcb_fullstatement,
                                    mcb_statement,
                                    _mcb_abc_value: abc.mcb_abcid,
                                    _mcb_skill_value: mcb_skillid,
                                    mcb_index: skillCounter[mcb_skillid] || 0
                                });
                                return;
                            }
                        }
                        
                    });

                  
                keySkillsAchievements = keySkillsAchievements
                    .sort((a,b) => b.mcb_index - a.mcb_index)
                    .map((a, mcb_index) => ({...a, mcb_index}));
                cv =   {
                    ...(
                        cv || {
                        mcb_cvname: cvName,
                        mcb_CVRoles,
                        cvRoles,
                        mcb_CVABC,
                        achievements,
                        mcb_CVQualifications: role.mcb_TertiaryQualifications,
                        mcb_CVKeySkill: role.mcb_KeySkills,
                        mcb_cliftonstrength: role.mcb_cliftonstrength
                    }),
                    mcb_roletitle: role?.mcb_roletitle,
                    mcb_rolecompanyname: role?.mcb_rolecompanyname,
                    mcb_firstname: profileData?.firstname,
                    mcb_lastname: profileData?.lastname,
                    mcb_email: profileData?.emailaddress1,
                    mcb_phone: profileData?.telephone1,
                    mcb_title: role?.mcb_roletitle,
                    mcb_mainqualification: cvMainQualifications,
                    mcb_profilesummary: profileData?.adx_publicprofilecopy,
                    mcb_currentcity: profileData?.address1_city,
                    mcb_linkedin: profileData?.mcb_linkedin,
                    mcb_sectors: profileData?.sectors
                        .map(sectorId => {
                            let sector = Sectors.find(s => s.value === Number(sectorId));
                            if (sector?.label) return sector.label;
                            sector = LegacySectors.find(s => s.value === Number(sectorId));
                            if (sector?.label) return sector.label;
                            return false
                        }).filter(a => a).join(", "),
                    keySkillsAchievements
                    
                };
            }
            setFormData((oldFormData) => ({
                ...role,
                ...oldFormData,
                cv: {
                    ...cv,
                    skills: (Array.isArray(cv?.mcb_CVKeySkill)? cv.mcb_CVKeySkill.map(({mcb_skillid}) => mcb_skillid) : []),
                    cliftonstrengths: (Array.isArray(cv?.mcb_cliftonstrength)? cv.mcb_cliftonstrength.split(",") : []).filter(a => a && !isNaN(a)).map(a => Number(a)),
                    achievements: (Array.isArray(cv?.mcb_CVABC)? cv.mcb_CVABC.map(({mcb_abcid}) => mcb_abcid) : []),
                    qualifications: (Array.isArray(cv?.mcb_CVQualifications)? cv.mcb_CVQualifications.map(({mcb_qualificationid}) => mcb_qualificationid) : []),
                    cvRoles: (Array.isArray(cv?.mcb_CVRoles)? cv.mcb_CVRoles.map(({mcb_roleid}) => mcb_roleid) : [])
                },
                CV: oldFormData.CV || cv.mcb_cvid || "new-cv",
                Duplicate: oldFormData?.Duplicate,
            }));
            setLoading(false);
        }

    }, [
        cvName, role, profileData, formData.CV, formData.Duplicate,
        roles, skills, qualifications, responsibilities, abcs, cvs, cvskillachievements,
        roleLoading, abcLoading, responsibilityLoading, cvLoading, skillLoading
    ]);

    const updateRequirements = React.useCallback(async () => {
        if (!formData?.mcb_roleid) throw new Error(`Unknown ${roleStatusName} Role`);

        const formRequirements = (formData || {})?.requirements;

        if (!(Array.isArray(formRequirements) && formRequirements?.length)) return false;
        
        setLoading(true);
        const responsibilityChanges = formRequirements.map(({achievements, ...resp}, mcb_index) => {
            return {
                ...resp,
                _mcb_role_value: formData?.mcb_roleid,
                mcb_index,
                mcb_ABCResponsibility: achievements
            };
        });
        responsibilities.forEach(({ _mcb_role_value, mcb_responsibilityid}) => {
            if (
                _mcb_role_value === formData?.mcb_roleid
                && formRequirements.every((fr) => fr.mcb_responsibilityid !== mcb_responsibilityid)
            ) {
                responsibilityChanges.push({mcb_responsibilityid, deleted: true});
            }
        });

        const role = {mcb_roleid: formData?.mcb_roleid, mcb_rolestatus: roleStatus};
        const mcb_KeySkills = (formData?.skills || []).map((mcb_skillid) => ({ mcb_skillid }));
        const mcb_TertiaryQualifications = (formData?.qualifications || []).map((mcb_qualificationid) => ({ mcb_qualificationid }));
        const mcb_cliftonstrength = Array.isArray(formData?.cliftonstrengths) && formData?.cliftonstrengths?.length? formData?.cliftonstrengths.join(",") : null;

        if (mcb_KeySkills?.length) role.mcb_KeySkills = mcb_KeySkills;
        if (mcb_cliftonstrength?.length) role.mcb_cliftonstrength = mcb_cliftonstrength;
        if (mcb_TertiaryQualifications?.length) role.mcb_TertiaryQualifications = mcb_TertiaryQualifications;
        let errors = [];
        await Promise.allSettled([
            processResponsibilityChanges(responsibilityChanges),
            updateRole(role)
        ]).then(promises => {
            if (promises[0].status === "rejected") {
                if (typeof promises[0].reason  && Array.isArray(promises[0].reason.errors)) {
                    errors = errors.concat(promises[0].reason.errors);
                } else {
                    errors = errors.concat(promises[0].reason);
                }
            }
            if (promises[1].status === "rejected") {
                errors.push(promises[1].reason);
            }
        });
        setLoading(false);
        if (errors.length) {
            const err = AggregateError(errors, `Failed to update ${roleStatusName} Role Requirements`);
            err.UserReadableError = true;
            throw err;
        } 
        return true;
    }, [formData, responsibilities, processResponsibilityChanges, updateRole, roleStatus, roleStatusName]);

    const updateCV = React.useCallback(async() => {
        if (!formData?.mcb_roleid) throw UserReadableError(`Unknown ${roleStatusName} Job`);
        const cvId = await _updateCV();

        if (cvId && formData?._mcb_cv_value !== cvId) {
            if (formData.mcb_roleid && cvId !== formData._mcb_cv_value) {
                await updateRole({ mcb_roleid: formData.mcb_roleid, _mcb_cv_value: cvId });
            }
        }
    }, [formData, _updateCV, updateRole, roleStatusName]);

    return { formData, setFormData, setCVBytes, loading, setLoading, updateRequirements, updateCV };
}